// node_modules
import { FC, useCallback, useEffect, useState } from "react";
// Components
import { EditableMarkdown } from "Components";
// Controllers
import { CaseControllerSingleton } from "Controllers";
// Enums
import { ToastTypeEnum } from "Enums";
// Helpers
import { ToastHelperSingleton } from "Helpers";
// Custom hooks
import { useDebounce } from "Hooks";
// styles
import styles from "./intake.module.scss";

export type TCaseDeliveryIntakeOverrideProps = {
    caseId: number,
    caseDeliveryIntakeOverride?: string,
    isEditMode: boolean
};

export const CaseDeliveryIntakeOverride: FC<TCaseDeliveryIntakeOverrideProps> = ({caseId, caseDeliveryIntakeOverride, isEditMode}: TCaseDeliveryIntakeOverrideProps) => {
    // State
    const [deliveryIntakeOverride, setDeliveryIntakeOverride] = useState<string | undefined>(undefined);

    // Const
    const debouncedCaseDeliveryIntakeOverride = useDebounce(deliveryIntakeOverride, 500);

    // Logic
    const updateCaseDeliveryIntakeOverrideAsync = useCallback(async (newCaseDeliveryIntakeOverride: string) => {
        // safety-checks
        if (newCaseDeliveryIntakeOverride === null || newCaseDeliveryIntakeOverride === undefined || deliveryIntakeOverride === null || deliveryIntakeOverride === undefined) { return; }
        setDeliveryIntakeOverride(undefined);

        // call server to update case delivery intake override
        const isSuccess: boolean = await CaseControllerSingleton
            .updateCaseDeliveryIntakeOverrideAsync(
                caseId, 
                newCaseDeliveryIntakeOverride
        );

        // tell user about update result 
        if (!isSuccess) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "Was not able to update case delivery functions and environments.");
        } else {
            ToastHelperSingleton.showToast(ToastTypeEnum.Success, "Successfully updated case delivery functions and environments.");
        }
        
        return;
    }, [caseId, deliveryIntakeOverride]);

    useEffect(() => {
        setDeliveryIntakeOverride(undefined);
    }, [caseId, caseDeliveryIntakeOverride]);

    useEffect(() => {
        // do not trigger edit at first render, when switching to a new case
        // or when displaying a new case
        if (debouncedCaseDeliveryIntakeOverride === null || debouncedCaseDeliveryIntakeOverride === undefined) { return; }

        // send new case delivery intake override to the server
        updateCaseDeliveryIntakeOverrideAsync(debouncedCaseDeliveryIntakeOverride);
    }, [debouncedCaseDeliveryIntakeOverride, updateCaseDeliveryIntakeOverrideAsync]);

    return (
        isEditMode || (!isEditMode && caseDeliveryIntakeOverride) ?
            <div className={styles.intakeItem}>
                <h4>Delivery functions and environments</h4>
                <EditableMarkdown 
                    source={caseDeliveryIntakeOverride ? caseDeliveryIntakeOverride : ""}
                    noSource={"No delivery functions and environments available."}
                    isEditable={isEditMode}
                    handleSourceEdited={setDeliveryIntakeOverride} 
                    entityId={caseId.toString()} />
            </div>
        :
            null
    );
};